import { EventCard, SportsEvent } from '../types';
import { defaultLogger } from '../utils/logger';
import { API_URL as API_BASE_URL } from '../config';

export interface CashoutOffer {
  parlay_id: string;
  cashout_amount: number;
  theoretical_value: number;
  cashout_vig: number;
  offer_expires_at: string;
  offer_id: string;
}

export interface CashoutResponse {
  success: boolean;
  payout_amount: number;
  parlay_id: string;
  transaction_time: string;
  status: string;
}

export interface ReferralCode {
  code: string;
  created_at: string;
  expires_at: string;
  is_active: boolean;
  total_uses: number;
  successful_referrals: number;
  max_uses: number;
}

export interface Referral {
  referral_code: string;
  reward_amount: number;
  reward_status: 'pending' | 'credited' | 'failed';
  reward_credited_at: string | null;
  first_parlay_placed: boolean;
  referred_email: string;
}

export interface UserReferralsResponse {
  success: boolean;
  codes: ReferralCode[];
  referrals: Referral[];
  total_earned: number;
  currency: string;
}

export const fetchEventCards = async (category?: string, subCategory?: string, page: number = 1, filter?: string): Promise<EventCard[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/event-cards`);
    if (category) {
      url.searchParams.append('category', category);
    }
    if (subCategory) {
      url.searchParams.append('sub_category', subCategory);
    }
    if (filter) {
      url.searchParams.append('filter', filter);
    }
    url.searchParams.append('page', page.toString());
    url.searchParams.append('page_size', '100');  // Increased from 50 to 100
    
    console.log('Fetching events with URL:', url.toString());
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      let errMsg = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.clone().json();
        const reqId = body?.request_id;
        if (reqId) {
          errMsg = `Failed to fetch events (id: ${reqId})`;
        }
      } catch {}
      defaultLogger.error(errMsg, { endpoint: '/event-cards', status: response.status });
      throw new Error(errMsg);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    defaultLogger.error('Error fetching event cards', error);
    throw error;
  }
};

export const fetchCashoutOffer = async (parlayId: string, userIssuer: string): Promise<CashoutOffer> => {
  try {
    const url = new URL(`${API_BASE_URL}/cashout/${parlayId}`);
    url.searchParams.append('user_issuer', userIssuer);
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      const errorData = await response.json();
      const reqId = errorData?.request_id;
      const msg = (errorData.error || 'Failed to fetch cashout offer') + (reqId ? ` (id: ${reqId})` : '');
      defaultLogger.error(msg, { endpoint: '/cashout/:id', status: response.status });
      throw new Error(msg);
    }
    
    return await response.json();
  } catch (error) {
    defaultLogger.error('Error fetching cashout offer', error);
    throw error;
  }
};

export const acceptCashoutOffer = async (offerId: string, userIssuer: string): Promise<CashoutResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cashout/${offerId}/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_issuer: userIssuer })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const reqId = errorData?.request_id;
      const msg = (errorData.error || 'Failed to accept cashout offer') + (reqId ? ` (id: ${reqId})` : '');
      defaultLogger.error(msg, { endpoint: '/cashout/:id/accept', status: response.status });
      throw new Error(msg);
    }
    
    return await response.json();
  } catch (error) {
    defaultLogger.error('Error accepting cashout offer', error);
    throw error;
  }
};

export const fetchUserReferrals = async (userIssuer: string): Promise<UserReferralsResponse> => {
  try {
    const url = new URL(`${API_BASE_URL}/user/referrals`);
    url.searchParams.append('user_issuer', userIssuer);
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      const errorData = await response.json();
      const reqId = errorData?.request_id;
      const msg = (errorData.error || 'Failed to fetch referrals') + (reqId ? ` (id: ${reqId})` : '');
      defaultLogger.error(msg, { endpoint: '/user/referrals', status: response.status });
      throw new Error(msg);
    }
    
    return await response.json();
  } catch (error) {
    defaultLogger.error('Error fetching user referrals', error);
    throw error;
  }
};

export const fetchSportsEvents = async (leagueId?: string, days?: number, page: number = 1, pageSize: number = 20): Promise<SportsEvent[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/sports/events`);
    if (leagueId) {
      url.searchParams.append('league_id', leagueId);
    }
    url.searchParams.append('days', (days || 7).toString());
    url.searchParams.append('page', page.toString());
    url.searchParams.append('page_size', pageSize.toString());

    const response = await fetch(url.toString());
    if (!response.ok) {
      let errMsg = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.clone().json();
        const reqId = body?.request_id;
        if (reqId) errMsg = `Failed to fetch sports events (id: ${reqId})`;
      } catch {}
      defaultLogger.error(errMsg, { endpoint: '/sports/events', status: response.status });
      throw new Error(errMsg);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    defaultLogger.error('Error fetching sports events', error);
    throw error;
  }
};

export const fetchSportsProps = async (subCategory?: string, page: number = 1, pageSize: number = 50): Promise<EventCard[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/sports/props`);
    if (subCategory) {
      url.searchParams.append('sub_category', subCategory);
    }
    url.searchParams.append('page', page.toString());
    url.searchParams.append('page_size', pageSize.toString());

    const response = await fetch(url.toString());
    if (!response.ok) {
      let errMsg = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.clone().json();
        const reqId = body?.request_id;
        if (reqId) errMsg = `Failed to fetch sports props (id: ${reqId})`;
      } catch {}
      defaultLogger.error(errMsg, { endpoint: '/sports/props', status: response.status });
      throw new Error(errMsg);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    defaultLogger.error('Error fetching sports props', error);
    throw error;
  }
};

export interface ParlaySuggestion {
  legs: Array<{
    ingest_market_id: string;
    outcome_label: string;
    event_title: string;
    market_question: string;
    market_icon?: string;
    event_image?: string;
    end_date?: string;
    outcome_price: number;
    sportsTeamData?: {
      team1: { logo?: string };
      team2: { logo?: string };
      selectedTeam: 0 | 1 | 2;
    };
  }>;
  stake_preview: number;
  total_odds: number;
  expected_payout: number;
  hundred_dollar_payout: number;
  engine: {
    name: string;
    version: string;
  };
  diagnostics: any;
  cache_hit: boolean;
}

export interface ParlaySuggestionsResponse {
  suggestions: ParlaySuggestion[];
}

export const fetchParlaySuggestions = async (): Promise<ParlaySuggestionsResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/parlays/suggestions/all`);

    if (!response.ok) {
      let errMsg = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.clone().json();
        const reqId = body?.request_id;
        if (reqId) errMsg = `Failed to fetch parlay suggestions (id: ${reqId})`;
      } catch {}
      defaultLogger.error(errMsg, { endpoint: '/parlays/suggestions/all', status: response.status });
      throw new Error(errMsg);
    }

    return await response.json();
  } catch (error) {
    defaultLogger.error('Error fetching parlay suggestions', error);
    throw error;
  }
};